import React, { useCallback, useEffect, useState } from 'react';
import {
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
  useTheme,
  IconButton,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Collapse,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATHS, ROUTES } from 'src/constants/navigateRoutes';
import HANA_LOGO from '../../assets/hanaLogo.png';
import styles from './SideNavBar.module.scss';
import UpgradeToPro from '../upgrade-to-pro/UpgradeToPro';
import { useTranslation } from 'react-i18next';
import styleVariables from '../../scss/_variables.scss';
import { CustomDivider } from '../custom-divider/CustomDivider';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CodeIcon from '@mui/icons-material/Code';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import TaskIcon from '@mui/icons-material/Task';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import { observer } from 'mobx-react-lite';
import ErrorBoundary from '../error-boundary/ErrorBoundry';
import {
  ORGANIZATION_ID_NOT_FOUND_ERROR,
  USER_DETAILS_NOT_FOUND_ERROR,
  UNHANDLED_ERROR,
} from 'src/constants/errors';
import CustomPersona from '../persona-by-plan/PersonaByPlan';
import { ROLES } from 'src/constants/Role';
import { canAccessDashboardRoles } from 'src/utils/dashboardRoleAccess';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HistoryIcon from '@mui/icons-material/History';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { userLogout } from 'src/services/auth';
import { Box } from '@mui/material';
import { PLAN_TYPES } from 'src/constants/cardsEnum';
import {
  getOrganizationCommercialPlanDisplayName,
  getOrganizationEffectivePlanDisplayName,
  hasActiveOrganizationBillingTrial,
  isOrganizationPlanAtLeast,
} from 'src/utils/organizationBilling';
import OrgSelector from '../org-selector/OrgSelector';
import { useStatsigClient } from '@statsig/react-bindings';
import { STATSIG_EVENTS } from 'src/constants/statsigEvents';
import { logStatsigEvent } from 'src/utils/statsigEvent';
import { TAG_LABEL_KEY } from 'src/constants/tag';
import { useAlertStore } from 'src/stores/AlertStore/AlertStore';
import { createReview, getLatestReview } from 'src/services/review';
import {
  REVIEW_SUBMIT_SUCCESS,
  REVIEW_DIALOG_TITLE,
} from 'src/constants/review';
import {
  getTextFieldStyles,
  STYLE_FOR_LARGE_DIALOG,
} from 'src/constants/Layout';
import { FormField } from 'src/components/custom-dialog/CustomDialog';
import InsightsIcon from '@mui/icons-material/Insights';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import { canUseSprintWorkspace } from 'src/utils/sprintUtils';
import { SPRINTS_ROUTE_KEY } from 'src/constants/sprint';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import {
  DISCOUNT_CODE_QUERY_PARAM,
  FREE_PLAN_UPSELL_DISCOUNT_CODE,
} from 'src/constants/invocationUpsell';
import { DASHBOARD_ANALYTICS_EVENTS } from 'src/constants/analytics';
import { trackDashboardEvent } from 'src/utils/analytics';
import { getSourceOperationsCapability } from 'src/services/sourceOperations';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MEMORY_TABS } from 'src/constants/memory';
import {
  getRevampNavigationPlacement,
  getSideNavAnalyticsTarget,
  getSideNavPath,
  isSideNavRouteActive,
  shouldRenderSideNavOrgSelector,
  shouldShowLegacyNavigation,
  shouldShowSourceHealthNavigation,
} from './sideNavVisibility';

type RevampNavigationSection = 'work' | 'knowledge' | 'manage';

interface SideNavBarOptions {
  text: string;
  route: string;
  icon: React.ReactNode;
  access: Array<keyof typeof ROLES>;
  isExternal?: boolean;
  isCritical?: boolean;
  isPowerful?: boolean;
  isLegacy?: boolean;
  hiddenFromRevampNavigation?: boolean;
  revampSection?: RevampNavigationSection;
  tourAnchorId?: string;
}

export interface SideNavBarProps {
  open: boolean;
  onChange: React.Dispatch<React.SetStateAction<boolean>>;
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const SideNavBar = observer(
  ({ open, onChange, collapsed, setCollapsed }: SideNavBarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
    const { organizationDetails } = useOrganizationStore();
    const { logout, getUserDetails, getRevampCapability, isRevampExperience } =
      useAuthStore();
    const userDetails = getUserDetails();
    const revampCapability = getRevampCapability();
    const revampExperience = isRevampExperience();
    const { notify } = useAlertStore();
    const { client } = useStatsigClient();

    const [ratingValue, setRatingValue] = useState<number | null>(null);
    const [isReviewOpen, setIsReviewOpen] = useState(false);
    const [reviewComment, setReviewComment] = useState('');
    const [sourceHealthEnabled, setSourceHealthEnabled] = useState(false);
    const actionReviewEnabled = Boolean(revampCapability?.reviewEnabled);
    const careOperationsEnabled = Boolean(
      revampCapability?.careOperationsEnabled,
    );
    const [legacyNavigationOpen, setLegacyNavigationOpen] = useState(false);
    const reviewFieldForStyle: FormField = {
      label: 'Comment',
      id: 'review-comment',
      value: '',
      type: 'textarea',
    };
    const REVIEW_TEXT_FIELD_STYLES = getTextFieldStyles(reviewFieldForStyle);
    const { t } = useTranslation();

    const handleChangePlan = async () => {
      const shouldAttachDiscountCode =
        getOrganizationCommercialPlanDisplayName(organizationDetails) ===
        PLAN_TYPES.FREE;
      const discountCodeQuery = shouldAttachDiscountCode
        ? `?${DISCOUNT_CODE_QUERY_PARAM}=${encodeURIComponent(FREE_PLAN_UPSELL_DISCOUNT_CODE)}`
        : '';

      navigate(`${PATHS.pricingPlans}${discountCodeQuery}`);
    };

    const shouldShowFreePlanDiscountCopy =
      getOrganizationCommercialPlanDisplayName(organizationDetails) ===
        PLAN_TYPES.FREE &&
      !hasActiveOrganizationBillingTrial(organizationDetails);
    const effectivePlanDisplayName =
      getOrganizationEffectivePlanDisplayName(organizationDetails);
    const activeBillingTrial = hasActiveOrganizationBillingTrial(
      organizationDetails,
    )
      ? (organizationDetails?.billingTrial ?? null)
      : null;
    const billingTrialDaysRemaining = activeBillingTrial
      ? Math.max(
          1,
          Math.ceil(
            (new Date(activeBillingTrial.endsAt).getTime() - Date.now()) /
              (24 * 60 * 60 * 1000),
          ),
        )
      : 0;

    const handleNavigation = async (route: string) => {
      const routePath = getSideNavPath(route);

      if (routePath === PATHS.unAuthorized) {
        await userLogout();
        logout();
      }

      if (routePath === PATHS.memory && userDetails) {
        logStatsigEvent(
          client,
          STATSIG_EVENTS.memoryModuleClick,
          userDetails._id,
          {
            orgId: userDetails.organizationId,
          },
        );
      }

      if (userDetails && routePath !== PATHS.unAuthorized) {
        trackDashboardEvent(
          DASHBOARD_ANALYTICS_EVENTS.navigationClick,
          {
            source: 'side_nav',
            target_route: getSideNavAnalyticsTarget(route),
          },
          {
            user: userDetails,
            pathname: location.pathname,
            search: location.search,
          },
        );
      }

      navigate(route);
    };

    const fetchLatestReview = useCallback(async () => {
      const { success, content } = await getLatestReview();

      if (success) {
        setRatingValue(content?.rating ?? null);
      }
    }, []);

    const sendReview = async () => {
      if (!ratingValue) {
        return;
      }

      const { success } = await createReview({
        rating: ratingValue,
        reviewComment,
      });

      if (success) {
        notify(REVIEW_SUBMIT_SUCCESS, 'success');
        await fetchLatestReview();
      } else {
        notify(UNHANDLED_ERROR, 'error');
      }

      setReviewComment('');
    };

    const handleRatingChange = (_: unknown, value: number | null) => {
      setRatingValue(value);
      setIsReviewOpen(true);
    };

    const handleReviewClose = async () => {
      setIsReviewOpen(false);
      await sendReview();
    };

    useEffect(() => {
      fetchLatestReview();
    }, [fetchLatestReview]);

    useEffect(() => {
      let active = true;

      if (!revampExperience) {
        setSourceHealthEnabled(false);

        return () => {
          active = false;
        };
      }

      // Clear the previous organization's capability before resolving the new
      // organization so a workspace switch cannot briefly expose stale access.
      setSourceHealthEnabled(false);

      void getSourceOperationsCapability()
        .then((response) => {
          if (active) {
            setSourceHealthEnabled(Boolean(response.content?.enabled));
          }
        })
        .catch(() => {
          if (active) setSourceHealthEnabled(false);
        });

      return () => {
        active = false;
      };
    }, [revampExperience, userDetails?.organizationId]);

    const shouldDisplaySprintsModule = canUseSprintWorkspace(
      revampExperience,
      revampCapability?.sprintWorkspaceEnabled,
    );
    const sideNavBarOptions: SideNavBarOptions[] = [
      {
        text: t('connect_hana_api'),
        route: `${ROUTES.connectors}/${PATHS.exposeApiConnector}`,
        icon: <CodeIcon />,
        // Shared legacy capability, not a revamp-only or Hanabi-domain feature.
        // The same entry stays in normal navigation for non-rollout customers.
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: t('get_started', { defaultValue: 'Get Started' }),
        route: ROUTES.getStarted,
        icon: <RocketLaunchIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: t('overview'),
        route: ROUTES.overview,
        icon: <DashboardIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: revampExperience
          ? t('clarity', { defaultValue: 'Clarity' })
          : t('whats_happening', { defaultValue: "What's Happening" }),
        route: ROUTES.whatsHappening,
        icon: <InsightsIcon />,
        isLegacy: !revampExperience,
        revampSection: revampExperience ? 'work' : undefined,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      ...(revampExperience
        ? [
            {
              text: t('whats_happening', { defaultValue: "What's Happening" }),
              route: `${ROUTES.whatsHappening}?view=legacy`,
              icon: <InsightsIcon />,
              isLegacy: true,
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
          ]
        : []),
      {
        text: t('meeting_overview', { defaultValue: 'Meeting Overview' }),
        route: ROUTES.meetingOverview,
        icon: <EventNoteIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      ...(shouldShowSourceHealthNavigation(
        revampExperience,
        sourceHealthEnabled,
      )
        ? [
            {
              text: revampExperience
                ? t('revamp_shell.sources', { defaultValue: 'Sources' })
                : t('memory'),
              route: revampExperience
                ? `${ROUTES.memory}?tab=${MEMORY_TABS.sourceHealth}`
                : ROUTES.memory,
              icon: <PsychologyIcon />,
              isCritical: !revampExperience,
              revampSection: revampExperience
                ? ('knowledge' as const)
                : undefined,
              tourAnchorId: 'side-nav-memory-item',
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
          ]
        : []),
      ...(revampExperience
        ? [
            {
              text: t('revamp_shell.notes', { defaultValue: 'Notes' }),
              route: `${ROUTES.memory}?tab=${MEMORY_TABS.workspaceNotes}`,
              icon: <EditNoteOutlinedIcon />,
              revampSection: 'knowledge' as const,
              tourAnchorId: sourceHealthEnabled
                ? undefined
                : 'side-nav-memory-item',
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
            {
              text: t('memory'),
              route: `${ROUTES.memory}?tab=${MEMORY_TABS.generalMemoryList}`,
              icon: <PsychologyIcon />,
              isLegacy: true,
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
          ]
        : []),
      {
        text: revampExperience
          ? t('activity', { defaultValue: 'Activity' })
          : t('invocation_history'),
        route: ROUTES.invocationHistory,
        icon: <HistoryIcon />,
        revampSection: revampExperience ? 'manage' : undefined,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      ...(careOperationsEnabled
        ? [
            {
              text: t('action_review.care_operations', {
                defaultValue: 'Care operations',
              }),
              route: ROUTES.tasks,
              icon: <HealthAndSafetyOutlinedIcon />,
              revampSection: 'work' as const,
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
          ]
        : []),
      {
        text: actionReviewEnabled
          ? careOperationsEnabled
            ? t('action_review.actions_ledger', {
                defaultValue: 'Actions ledger',
              })
            : t('actions', { defaultValue: 'Actions' })
          : t('tasks'),
        route: careOperationsEnabled
          ? `${ROUTES.tasks}?surface=actions`
          : ROUTES.tasks,
        icon: <TaskIcon />,
        // Better Mind's governed Care surface is the primary lifecycle UI.
        // Keep the ordinary Actions ledger reachable for compatibility without
        // presenting a second competing work surface in the mock-aligned nav.
        isLegacy: revampExperience && careOperationsEnabled,
        revampSection: revampExperience ? 'work' : undefined,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      ...(shouldDisplaySprintsModule
        ? [
            {
              text: t(SPRINTS_ROUTE_KEY),
              route: ROUTES.sprints,
              icon: <ViewKanbanIcon />,
              isLegacy: !revampExperience,
              revampSection: revampExperience ? ('work' as const) : undefined,
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
          ]
        : []),
      {
        text: t('users'),
        route: ROUTES.users,
        icon: <GroupsIcon />,
        isLegacy: true,
        access: [ROLES.USER, ROLES.ADMIN, ROLES.SUPER_ADMIN],
      },
      {
        text: t('spaces'),
        route: ROUTES.spaces,
        icon: <WorkspacesIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      ...(revampExperience
        ? [
            {
              text: t('automations'),
              route: ROUTES.automation,
              icon: <BoltOutlinedIcon />,
              revampSection: 'work' as const,
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
            {
              text: t('instructions', { defaultValue: 'Instructions' }),
              route: ROUTES.instructions,
              icon: <TuneOutlinedIcon />,
              revampSection: 'manage' as const,
              access: [
                ROLES.USER,
                ROLES.ADMIN,
                ROLES.SUPER_ADMIN,
                ROLES.DOMAIN_OWNER,
              ],
            },
          ]
        : []),
      {
        text: t('report_groups'),
        route: ROUTES.reportGroups,
        icon: <SummarizeIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: t('saved_prompts'),
        route: ROUTES.savedPrompts,
        icon: <TextFieldsIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: t(TAG_LABEL_KEY),
        route: ROUTES.tags,
        icon: <LocalOfferIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: t('polls', { defaultValue: 'Polls' }),
        route: ROUTES.polls,
        icon: <HowToVoteIcon />,
        isLegacy: true,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: t('settings'),
        route: ROUTES.settings,
        icon: <SettingsIcon />,
        // Settings is owned by the account and workspace menus in the revamp.
        // The route remains available for bookmarks and legacy navigation.
        hiddenFromRevampNavigation: revampExperience,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
      {
        text: t('crm'),
        route: ROUTES.crm,
        icon: <ContactMailIcon />,
        isLegacy: true,
        access: [ROLES.SUPER_ADMIN],
      },
      {
        text: t('manual_verifications', {
          defaultValue: 'Manual Verification',
        }),
        route: ROUTES.manualVerifications,
        icon: <VerifiedUserIcon />,
        isLegacy: true,
        access: [ROLES.SUPER_ADMIN],
      },
      {
        text: t('aml_verifications', {
          defaultValue: 'AML Verification',
        }),
        route: ROUTES.amlVerifications,
        icon: <VerifiedUserIcon />,
        isLegacy: true,
        access: [ROLES.SUPER_ADMIN],
      },
      {
        text: t('high_value_clients'),
        route: ROUTES.highValueClients,
        icon: <InsightsIcon />,
        isLegacy: true,
        access: [ROLES.SUPER_ADMIN],
      },
    ];

    const isModuleRouteActive = (route: string) =>
      isSideNavRouteActive({
        route,
        pathname: location.pathname,
        search: location.search,
        careOperationsEnabled,
        revampExperience,
      });

    const additionalSideNavBarOptions: SideNavBarOptions[] = [
      {
        text: t('docs'),
        route: ROUTES.docs,
        icon: <TextSnippetIcon />,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
        isExternal: true,
      },
      {
        text: t('help_learn'),
        route: ROUTES.help,
        icon: <HelpIcon />,
        access: [
          ROLES.USER,
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.DOMAIN_OWNER,
        ],
      },
    ];

    if (!userDetails) {
      return <ErrorBoundary message={USER_DETAILS_NOT_FOUND_ERROR} />;
    }

    if (!organizationDetails) {
      return <ErrorBoundary message={ORGANIZATION_ID_NOT_FOUND_ERROR} />;
    }

    const IS_NOT_PRO_PLAN = !isOrganizationPlanAtLeast(
      organizationDetails,
      PLAN_TYPES.PRO,
    );
    const accessibleSideNavBarOptions = sideNavBarOptions.filter((option) =>
      canAccessDashboardRoles(userDetails, option.access),
    );
    const primarySideNavBarOptions = revampExperience
      ? accessibleSideNavBarOptions.filter(
          (option) => getRevampNavigationPlacement(option) === 'primary',
        )
      : accessibleSideNavBarOptions;
    const legacySideNavBarOptions = revampExperience
      ? accessibleSideNavBarOptions.filter(
          (option) => getRevampNavigationPlacement(option) === 'legacy',
        )
      : [];
    // A bookmarked legacy route must reveal its selected navigation item. This
    // preserves the established deep-link behavior while keeping the section
    // collapsed by default on revamp-owned routes.
    const activeLegacyNavigation = legacySideNavBarOptions.some((option) =>
      isModuleRouteActive(option.route),
    );
    const showLegacyNavigation = shouldShowLegacyNavigation(
      legacyNavigationOpen,
      activeLegacyNavigation,
    );
    const revampSetupOption = revampExperience
      ? accessibleSideNavBarOptions.find(
          (option) => option.route === ROUTES.getStarted,
        )
      : undefined;
    const revampNavigationSections: Array<{
      id: RevampNavigationSection;
      label: string | null;
    }> = [
      { id: 'work', label: null },
      {
        id: 'knowledge',
        label: t('revamp_shell.knowledge', { defaultValue: 'Knowledge' }),
      },
      {
        id: 'manage',
        label: t('revamp_shell.manage', { defaultValue: 'Manage' }),
      },
    ];
    const renderSideNavOption = ({
      text,
      route,
      icon,
      isCritical,
      isPowerful,
      tourAnchorId,
    }: SideNavBarOptions) => (
      <ListItem
        className={`${styles['module-item']} ${
          !revampExperience && isModuleRouteActive(route)
            ? styles['module-active']
            : ''
        }`}
        key={route}
        disablePadding
      >
        <ListItemButton
          selected={revampExperience && isModuleRouteActive(route)}
          aria-label={text}
          id={tourAnchorId}
          onClick={() => handleNavigation(`/${route}`)}
          sx={{
            borderRadius: '8px',
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 0 : 2,
            transition: 'background-color 0.3s ease-in-out',
            color: revampExperience
              ? isModuleRouteActive(route)
                ? 'primary.main'
                : 'text.secondary'
              : 'inherit',
          }}
        >
          <ListItemIcon
            sx={{
              color: revampExperience ? 'inherit' : styles.colorWhite,
              justifyContent: 'center',
              minWidth: 0,
              mr: !collapsed ? 2 : 0,
            }}
          >
            {icon}
          </ListItemIcon>
          {!collapsed && (
            <>
              <ListItemText primary={text} />
              {isCritical && (
                <Chip
                  label={t('critical')}
                  variant="outlined"
                  color="warning"
                  sx={{ fontSize: '12px' }}
                />
              )}
              {isPowerful && (
                <Chip
                  sx={{ fontSize: '12px' }}
                  label={t('powerful')}
                  variant="outlined"
                  color="info"
                />
              )}
            </>
          )}
        </ListItemButton>
      </ListItem>
    );

    return (
      <Drawer
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          gridTemplateColumns: 3,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: {
              xs: open
                ? collapsed
                  ? revampExperience
                    ? 72
                    : styleVariables.sidebarCollapsedWidthMd
                  : '70vw'
                : 0,
              md: revampExperience
                ? collapsed
                  ? 72
                  : 248
                : collapsed
                  ? styleVariables.sidebarCollapsedWidthMd
                  : styleVariables.sidebarOpenWidthMd,
              lg: revampExperience
                ? collapsed
                  ? 72
                  : 248
                : collapsed
                  ? styleVariables.sidebarCollapsedWidthLg
                  : styleVariables.sidebarOpenWidthLg,
              xl: revampExperience
                ? collapsed
                  ? 72
                  : 248
                : collapsed
                  ? styleVariables.sidebarCollapsedWidthXl
                  : styleVariables.sidebarOpenWidthXl,
            },
            transition: 'width 0.3s',
            overflowX: 'hidden',
            borderRight: revampExperience
              ? '1px solid'
              : `1px solid ${styleVariables.sealBrown}`,
            borderColor: revampExperience ? 'divider' : undefined,
            bgcolor: revampExperience ? 'background.paper' : undefined,
            borderRadius: 0,
          },
          overflow: 'hidden',
        }}
        anchor="left"
        SlideProps={{
          direction: 'right',
        }}
        variant={isLargeScreen ? 'permanent' : 'temporary'}
        open={isLargeScreen ? true : open}
        onClose={isLargeScreen ? undefined : () => onChange(false)}
      >
        {/* Top section: Toolbar with centered persona only in collapsed mode */}
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: revampExperience ? 1.5 : 0,
            pt: revampExperience ? 1.25 : 0,
          }}
          disableGutters
        >
          <Box
            sx={{
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent:
                  revampExperience && !collapsed ? 'flex-start' : 'center',
                alignItems: 'center',
                gap: revampExperience ? 1.25 : 2,
                mr: 'auto',
              }}
            >
              <CustomPersona
                currentPlan={effectivePlanDisplayName}
                sx={revampExperience ? { width: 38, height: 38 } : undefined}
                sxDecoratorIcon={{
                  ...(revampExperience
                    ? { display: 'none' }
                    : {
                        height: '16px',
                        width: '16px',
                        padding: '2px',
                        top: -4,
                        right: -4,
                      }),
                }}
              />
              {!collapsed && (
                <Box minWidth={0}>
                  {revampExperience ? (
                    <Box>
                      <Box
                        component="p"
                        sx={{
                          m: 0,
                          color: 'text.primary',
                          fontSize: 18,
                          fontWeight: 700,
                          lineHeight: 1.1,
                        }}
                      >
                        hana
                      </Box>
                      <Box
                        component="p"
                        sx={{
                          m: 0,
                          mt: 0.25,
                          color: 'text.secondary',
                          fontSize: 11,
                          lineHeight: 1.2,
                        }}
                      >
                        {t('revamp_shell.workspace_assistant', {
                          defaultValue: 'Workspace assistant',
                        })}
                      </Box>
                    </Box>
                  ) : (
                    <IconButton
                      aria-label="Go to overview"
                      onClick={() => navigate(PATHS.overview)}
                      sx={{ padding: 0 }}
                    >
                      <img
                        className={styles['hana-logo']}
                        src={HANA_LOGO}
                        alt="Hana logo"
                      />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
            {/* When expanded, keep additional elements */}
            {!revampExperience && !collapsed && isLargeScreen && (
              <IconButton
                aria-label="Collapse sidebar"
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsed((prev) => !prev);
                }}
                sx={{ color: revampExperience ? 'text.secondary' : 'white' }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
          </Box>
          {/* In collapsed mode, render the toggle icon in its own container (not in the scroller) */}
          {!revampExperience && collapsed && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <IconButton
                aria-label="Expand sidebar"
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsed((prev) => !prev);
                }}
                sx={{ color: revampExperience ? 'text.secondary' : 'white' }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>

        <CustomDivider sx={{ mb: 2, mt: 2 }} orientation="horizontal" />

        {shouldRenderSideNavOrgSelector(revampExperience, isLargeScreen) && (
          <Box
            className={styles.orgSelector}
            id={revampExperience ? undefined : 'side-nav-tour-anchor'}
          >
            <OrgSelector revamp={revampExperience} />
            <CustomDivider sx={{ mb: 2, mt: 2 }} orientation="horizontal" />
          </Box>
        )}

        {/* List content that scrolls */}
        <div
          id={revampExperience ? 'side-nav-tour-anchor' : undefined}
          className={`${styles['modules-wrapper']} ${
            revampExperience ? styles['revamp-modules-wrapper'] : ''
          } ${collapsed ? styles['no-scrollbar'] : ''}`}
          style={{
            overflowY: 'auto', // allow scrolling in both collapsed and expanded states
          }}
        >
          {revampExperience ? (
            <>
              {isModuleRouteActive(ROUTES.getStarted) && revampSetupOption && (
                <Box sx={{ pb: 1 }}>
                  <List component="nav" aria-label="Setup navigation">
                    {renderSideNavOption({
                      ...revampSetupOption,
                      text: t('setup_hana', { defaultValue: 'Set up Hana' }),
                    })}
                  </List>
                  <CustomDivider sx={{ mt: 1 }} orientation="horizontal" />
                </Box>
              )}
              {revampNavigationSections.map(({ id, label }) => {
                const sectionOptions = primarySideNavBarOptions.filter(
                  (option) => option.revampSection === id,
                );

                if (sectionOptions.length === 0) return null;

                return (
                  <Box key={id} sx={{ mt: label ? 2 : 0 }}>
                    {!collapsed && label && (
                      <Box
                        component="p"
                        sx={{
                          m: 0,
                          px: 2.5,
                          pb: 0.5,
                          color: 'text.secondary',
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {label}
                      </Box>
                    )}
                    <List
                      component="nav"
                      aria-label={
                        label ??
                        t('revamp_shell.work', { defaultValue: 'Work' })
                      }
                    >
                      {sectionOptions.map(renderSideNavOption)}
                    </List>
                  </Box>
                );
              })}
            </>
          ) : (
            <List component="nav" aria-label="Main Navigation">
              {primarySideNavBarOptions.map(renderSideNavOption)}
            </List>
          )}
          {revampExperience && legacySideNavBarOptions.length > 0 && (
            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ListItem disablePadding>
                <ListItemButton
                  aria-label={t(
                    showLegacyNavigation
                      ? 'revamp_shell.collapse_legacy_navigation'
                      : 'revamp_shell.expand_legacy_navigation',
                    {
                      defaultValue: showLegacyNavigation
                        ? 'Collapse legacy navigation'
                        : 'Expand legacy navigation',
                    },
                  )}
                  aria-expanded={showLegacyNavigation}
                  onClick={() => setLegacyNavigationOpen((open) => !open)}
                  sx={{
                    borderRadius: '8px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 0 : 2,
                    color: 'text.secondary',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      justifyContent: 'center',
                      minWidth: 0,
                      mr: collapsed ? 0 : 2,
                    }}
                  >
                    <HistoryIcon />
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={t('revamp_shell.legacy', {
                          defaultValue: 'Legacy',
                        })}
                        secondary={t('revamp_shell.legacy_description', {
                          defaultValue: 'Previous dashboard tools',
                        })}
                        primaryTypographyProps={{ sx: { fontWeight: 650 } }}
                        secondaryTypographyProps={{ sx: { fontSize: 11 } }}
                      />
                      {showLegacyNavigation ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse in={showLegacyNavigation} timeout="auto" unmountOnExit>
                <List
                  component="nav"
                  aria-label={t('revamp_shell.legacy', {
                    defaultValue: 'Legacy',
                  })}
                >
                  {legacySideNavBarOptions.map(renderSideNavOption)}
                </List>
              </Collapse>
            </Box>
          )}
          {!revampExperience && <CustomDivider orientation="horizontal" />}
          {!revampExperience && (
            <List component="nav" aria-label="Additional Navigation">
              {additionalSideNavBarOptions.map(
                ({ text, route, icon, access, isExternal }) =>
                  canAccessDashboardRoles(userDetails, access) && (
                    <ListItem
                      className={`${styles['module-item']} ${
                        !revampExperience && `/${route}` === location.pathname
                          ? styles['module-active']
                          : ''
                      }`}
                      key={text}
                      disablePadding
                    >
                      {isExternal ? (
                        <ListItemButton
                          selected={
                            revampExperience &&
                            `/${route}` === location.pathname
                          }
                          aria-label={text}
                          component="a"
                          href={route}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: revampExperience
                              ? 'text.secondary'
                              : styles.colorWhite,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            px: collapsed ? 0 : 2,
                            transition: 'background-color 0.3s ease-in-out',
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: revampExperience
                                ? 'inherit'
                                : styles.colorWhite,
                              justifyContent: 'center',
                              minWidth: 0,
                              mr: !collapsed ? 2 : 0,
                            }}
                          >
                            {icon}
                          </ListItemIcon>
                          {!collapsed && <ListItemText primary={text} />}
                        </ListItemButton>
                      ) : (
                        <ListItemButton
                          selected={
                            revampExperience &&
                            `/${route}` === location.pathname
                          }
                          aria-label={text}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            px: collapsed ? 0 : 2,
                            transition: 'background-color 0.3s ease-in-out',
                            color: revampExperience
                              ? 'text.secondary'
                              : 'inherit',
                          }}
                          onClick={() => handleNavigation(route)}
                        >
                          <ListItemIcon
                            sx={{
                              color: revampExperience
                                ? 'inherit'
                                : styles.colorWhite,
                              justifyContent: 'center',
                              minWidth: 0,
                              mr: !collapsed ? 2 : 0,
                            }}
                          >
                            {icon}
                          </ListItemIcon>
                          {!collapsed && <ListItemText primary={text} />}
                        </ListItemButton>
                      )}
                    </ListItem>
                  ),
              )}
            </List>
          )}
        </div>

        {/* Only show upgrade component if not collapsed */}
        {!collapsed && !revampExperience && (
          <div className={styles['upgrade-container']}>
            {activeBillingTrial && (
              <UpgradeToPro
                title={t('hana_pro_trial_active_title', {
                  defaultValue: 'Hana PRO trial active',
                })}
                subTitle={t('hana_pro_trial_active_subtitle', {
                  defaultValue:
                    '{{count}} day(s) left on your workspace PRO trial. Upgrade anytime to keep PRO features without interruption.',
                  count: billingTrialDaysRemaining,
                })}
                ctaText={t('hana_pro_trial_upgrade_cta', {
                  defaultValue: 'Upgrade before trial ends',
                })}
                handleChangePlan={handleChangePlan}
              />
            )}
            {!activeBillingTrial && IS_NOT_PRO_PLAN && (
              <UpgradeToPro
                title={t('sky_rocket_productivity')}
                subTitle={
                  shouldShowFreePlanDiscountCopy
                    ? t(
                        'upgrade_for_unlimited_interactions_with_discount_code',
                        {
                          code: FREE_PLAN_UPSELL_DISCOUNT_CODE,
                        },
                      )
                    : t('upgrade_for_unlimited_interactions')
                }
                ctaText={t('upgrade_to_pro')}
                handleChangePlan={handleChangePlan}
              />
            )}
          </div>
        )}

        {!collapsed && !revampExperience && (
          <div className={styles['rating-container']}>
            <Rating
              name="dashboard-feedback"
              value={ratingValue}
              onChange={handleRatingChange}
              sx={{ color: styleVariables.premiumGold }}
            />
          </div>
        )}

        <Dialog
          open={isReviewOpen}
          onClose={handleReviewClose}
          PaperProps={{ sx: STYLE_FOR_LARGE_DIALOG }}
        >
          <DialogTitle>{REVIEW_DIALOG_TITLE}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <TextField
              multiline
              fullWidth
              label="Please tell us why you gave this rating? We want to serve you better."
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              InputProps={{ style: REVIEW_TEXT_FIELD_STYLES.textareaInput }}
              sx={{ flex: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleReviewClose}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Drawer>
    );
  },
);

export default SideNavBar;
